// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

import { StatTileData } from '../components/StatTile';
import { BarRow } from '../components/BarListPanel';
import type { UserStats, RoleBreakdown } from '../../shared/users';
import type { ProcedureStats, DomainBreakdown } from '../../shared/procedures';

const ROLE_TONES: BarRow['tone'][] = ['accent', 'info', 'ok', 'idle'];

// Fixed identity, matching the agreed mockup — Cooling is always info-blue,
// not "whichever domain the API happened to list first."
const DOMAIN_TONES: Record<string, BarRow['tone']> = {
  cooling: 'info',
  power: 'accent',
  shared: 'ok',
  other: 'warn',
};

export function toUserTiles(stats: UserStats): StatTileData[] {
  return [
    { label: 'Active accounts', value: stats.activeAccounts, sub: 'Can sign in to all facilities' },
    {
      label: 'Pending invites',
      value: stats.pendingInvites,
      sub: 'Invited or awaiting activation',
    },
    {
      label: 'Elevated access',
      value: stats.elevatedAccess,
      tone: 'accent',
      sub: 'Admins — every facility',
    },
    {
      label: 'No site access',
      value: '—',
      sub: 'Not available from this endpoint',
      mocked: true,
    },
  ];
}

export function toUserBars(roleBreakdown: RoleBreakdown[]): BarRow[] {
  return roleBreakdown.map((r, i) => ({
    label: r.label,
    count: r.count,
    pct: r.pct,
    tone: ROLE_TONES[i % ROLE_TONES.length],
  }));
}

export function toProcedureTiles(stats: ProcedureStats): StatTileData[] {
  const approvedPct = stats.total > 0 ? Math.round((stats.approved / stats.total) * 100) : 0;
  return [
    { label: 'Procedures', value: stats.total, sub: 'SOPs, MOPs, EOPs and policies' },
    {
      label: 'Approved',
      value: stats.approved,
      tone: 'ok',
      sub: `${approvedPct}% of the library is live`,
    },
    { label: 'In review', value: stats.inReview, tone: 'warn', sub: 'Waiting on an SME sign-off' },
    {
      label: 'Drafts and rejects',
      value: stats.draft + stats.deprecated,
      sub: `${stats.draft} draft · ${stats.deprecated} deprecated`,
    },
  ];
}

export function toProcedureBars(domainBreakdown: DomainBreakdown[]): BarRow[] {
  return domainBreakdown.map(d => ({
    label: d.label,
    count: d.count,
    pct: d.pct,
    tone: DOMAIN_TONES[d.domain] ?? 'idle',
  }));
}
